import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { useIsMobile, useTheme } from '../../../../../hooks';
import { DateTimePickerProps } from './model';
import { useDateTimePickerAnchor, useDateTimePickerValue, usePopperProps } from './hooks';
import { formatValueForOnChange, getDefaultLabelDateFormat, getDefaultValueDateFormat } from './utils';
import { DateTimePickerField } from './DateTimePickerField';
import { DateTimePickerContent } from './DateTimePickerContent';

export const DateTimePicker = React.forwardRef<HTMLInputElement, DateTimePickerProps & { type: 'dateTimePicker' }>(
  (
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      type,
      pickerType = 'dateTime',
      name,
      value: _value,
      disabled,
      onChange,
      size,
      sx,
      onMonthChange,
      shouldDisableDate,
      labelDateFormat,
      valueDateFormat,
      placeholder = 'Pick date...',
      disablePast = true,
      disableFuture = false,
    },
    ref,
  ) => {
    // Hooks
    const theme = useTheme();
    const isMobile = useIsMobile();
    const [innerValue, setInnerValue] = useDateTimePickerValue(_value, valueDateFormat || getDefaultValueDateFormat(pickerType));
    const { anchorRef, anchorEl, fieldRef, updateAnchorEl } = useDateTimePickerAnchor(ref);
    const [isOpen, setOpen] = React.useState(false);
    const popperProps = usePopperProps(anchorEl, anchorRef);

    // Computed values
    const finalLabelDateFormat = labelDateFormat || getDefaultLabelDateFormat(pickerType);
    const finalValueDateFormat = valueDateFormat || getDefaultValueDateFormat(pickerType);

    // Handlers
    const handleToggle = React.useCallback(() => {
      setOpen((prev) => !prev);
    }, []);

    const handleAccept = React.useCallback(
      (newValue: Date | null) => {
        setOpen(false);
        onChange?.(formatValueForOnChange(newValue, finalValueDateFormat));
      },
      [onChange, finalValueDateFormat],
    );

    const handleClose = React.useCallback(() => {
      setOpen(false);
    }, []);

    const handleOpen = React.useCallback(() => {
      updateAnchorEl();
    }, [updateAnchorEl]);

    // Field component
    const FieldComponent = React.useCallback(
      () => (
        <DateTimePickerField
          active={isOpen}
          disabled={disabled}
          fieldRef={fieldRef}
          innerValue={innerValue}
          isMobile={isMobile}
          labelDateFormat={finalLabelDateFormat}
          name={name}
          onToggle={handleToggle}
          placeholder={placeholder}
          size={size}
          sx={sx}
          value={_value}
        />
      ),
      [disabled, fieldRef, finalLabelDateFormat, handleToggle, innerValue, isMobile, isOpen, name, placeholder, size, sx, _value],
    );

    return (
      <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
        <DateTimePickerContent
          anchorEl={anchorEl}
          anchorRef={anchorRef}
          disableFuture={disableFuture}
          disablePast={disablePast}
          fieldComponent={FieldComponent}
          innerValue={innerValue}
          isOpen={isOpen}
          onAccept={handleAccept}
          onChange={setInnerValue}
          onClose={handleClose}
          onMonthChange={onMonthChange}
          onOpen={handleOpen}
          pickerType={pickerType}
          popperProps={popperProps}
          shouldDisableDate={shouldDisableDate}
          theme={theme}
        />
      </LocalizationProvider>
    );
  },
);
